if (!customElements.get("product-modal-gallery")) {
  class ProductModalGallery extends HTMLElement {
    constructor() {
      super();
      this.galleryCore = window.IWCProductGalleryCore;
      this.swiper = undefined;
      this.thumbnailSwiper = undefined;
      this.content = this.querySelector("[data-modal-gallery-content]");
      this.fabricNotice = this.querySelector("[data-gallery-fabric-notice]");
      this.contentLoaded = false;
      this.loadingPromise = null;
      this.selectedFabric = this.dataset.selectedFabric || "";
      this.selectedFamily = this.dataset.selectedFamily || "";
      this.selectedOptionTokens = this.galleryCore.parseTokens(this.dataset.selectedOptionTokens);
      this.resolvedMediaIndexes = null;
      this.isFullImage = false;
      // Zoom dziala tylko w trybie pelnoekranowym. skala 1 = zdjecie
      // dopasowane do ekranu, czyli stan wejsciowy trybu full-image.
      this.zoom = { skala: 1, x: 0, y: 0 };
      this.zoomMax = 4;
      this.hoverSkala = 2.5;
      this.listen();

      this._configurationHandler = (event) => {
        if (this.fabricNotice) {
          this.fabricNotice.hidden = event.detail.gallery?.fabricMismatch !== true;
          this.fabricNotice.textContent = event.detail.gallery?.materialMismatch
            ? this.fabricNotice.dataset.materialMessage : this.fabricNotice.dataset.fabricMessage;
        }
        this.selectedFabric = event.detail.fabricHandle || "";
        this.selectedFamily = event.detail.familyHandle || "";
        this.selectedOptionTokens = Array.isArray(event.detail.optionTokens)
          ? event.detail.optionTokens
          : this.galleryCore.parseTokens(event.detail.optionTokens);
        this.resolvedMediaIndexes = Array.isArray(event.detail.gallery?.mediaIndexes)
          ? event.detail.gallery.mediaIndexes.map((index) => String(index))
          : null;
        if (this.contentLoaded) {
          this.filterMedia();
          this.swiper?.slideTo(0, 0);
          this.syncActiveState();
          this.playActiveVideo();
        }
      };

      window.addEventListener("pdpConfigurationChange", this._configurationHandler);
    }

    loadContent() {
      if (this.contentLoaded) return Promise.resolve();
      if (this.loadingPromise) return this.loadingPromise;
      if (!this.content?.dataset.lazyUrl) return Promise.resolve();

      this.loadingPromise = fetch(this.content.dataset.lazyUrl)
        .then((response) => response.text())
        .then((html) => {
          this.content.innerHTML = html;
          this.contentLoaded = true;
          this.cacheContentElements();
          this.loadingPromise = null;
          this.filterMedia();
          this.initSwiper();
          this.initThumbnailSwiper();
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
            this.updateSwipers();
          }, 100);
        })
        .catch(() => {
          this.loadingPromise = null;
        });

      return this.loadingPromise;
    }

    filterMedia() {
      const result = this.galleryCore.filterPairedSlides({
        slides: this.modalSlides,
        thumbnailSlides: this.thumbnailSlides,
        mainWrapper: this.modalWrapper,
        thumbnailWrapper: this.thumbnailWrapper,
        selectedFabric: this.selectedFabric,
        selectedFamily: this.selectedFamily,
        selectedOptionTokens: this.selectedOptionTokens,
        resolvedMediaIndexes: this.resolvedMediaIndexes,
        fallbackToAll: true,
        indexThumbnails: true,
      });

      this.classList.toggle("product__modal-gallery--single", result.visibleCount <= 1);
      this.updateSwipers();
      this.syncActiveState();
    }

    get modalSlides() {
      return this.allSlides || Array.from(this.querySelectorAll("[data-modal-gallery-slide]"));
    }

    get thumbnailSlides() {
      return this.allThumbnailSlides || Array.from(this.querySelectorAll(".product__modal-gallery-thumbnail-slide"));
    }

    get modalWrapper() {
      return this.mainWrapper || this.querySelector("[data-modal-gallery-main] .swiper-wrapper");
    }

    get thumbnailWrapper() {
      return this.thumbnailWrapperElement || this.querySelector("[data-modal-gallery-thumbnails] .swiper-wrapper");
    }

    cacheContentElements() {
      this.main = this.querySelector("[data-modal-gallery-main]");
      this.mainContainer = this.main?.querySelector(".swiper-container");
      this.mainWrapper = this.mainContainer?.querySelector(".swiper-wrapper");
      this.thumbnails = this.querySelector("[data-modal-gallery-thumbnails]");
      this.thumbnailContainer = this.thumbnails?.querySelector(".swiper-container");
      this.thumbnailWrapperElement = this.thumbnailContainer?.querySelector(".swiper-wrapper");
      this.prev = this.querySelector(".product__modal-gallery-nav--prev");
      this.next = this.querySelector(".product__modal-gallery-nav--next");
      this.allSlides = Array.from(this.querySelectorAll("[data-modal-gallery-slide]"));
      this.allThumbnailSlides = Array.from(this.querySelectorAll(".product__modal-gallery-thumbnail-slide"));
    }

    updateSwipers() {
      this.swiper?.update();
      this.swiper?.navigation?.update();
      this.thumbnailSwiper?.update();
    }

    initSwiper() {
      if (this.swiper || !this.mainContainer) return;
      this.swiper = new Swiper(this.mainContainer, {
        loop: false,
        grabCursor: false,
        noSwipingSelector: ".swiper-no-swiping, model-viewer",
        speed: 500,
        spaceBetween: 8,
        slidesPerView: 1,
        navigation: {
          nextEl: this.next,
          prevEl: this.prev,
        },
        on: {
          slideChange: () => {
            this.syncActiveState();
          },
          slideChangeTransitionEnd: () => {
            this.playActiveVideo();
          },
        },
      });
    }

    initThumbnailSwiper() {
      if (this.thumbnailSwiper || !this.thumbnailContainer) return;

      this.thumbnailSwiper = this.galleryCore.createThumbnailSwiper(this.thumbnailContainer);
    }

    hydrateVideo(video, shouldPlay = false) {
      if (!video) return;
      if (video.dataset.videoLoaded === "true") {
        if (shouldPlay) {
          video.play().catch(() => {});
        }
        return;
      }

      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.videoLoaded = "true";
      video.load();
      if (shouldPlay) {
        video.play().catch(() => {});
      }
    }

    pauseVideos() {
      this.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    }

    playActiveVideo() {
      this.pauseVideos();
      const video = this.querySelector("[data-modal-gallery-main] .swiper-slide-active video");
      if (video) {
        this.hydrateVideo(video, true);
      }
    }

    /* W trybie pelnym powiekszamy zdjecie nawet 4x, a galeria na telefonie laduje
       wersje ok. 820 px. Podmieniamy na pelna rozdzielczosc z CDN Shopify
       (width=2048 - CDN odda oryginal, jesli jest mniejszy). Raz na zdjecie. */
    zaladujPelnaRozdzielczosc() {
      const obraz = this.warstwaZoomu()?.querySelector("img");
      if (!obraz || obraz.dataset.pelnaRozdzielczosc) return;
      const zrodlo = obraz.currentSrc || obraz.src;
      if (!zrodlo || !/cdn\/shop\/|cdn\.shopify\.com/.test(zrodlo)) return;
      const url = new URL(zrodlo, window.location.href);
      url.searchParams.set("width", "2048");
      obraz.dataset.pelnaRozdzielczosc = "true";
      const podglad = new Image();
      podglad.onload = () => {
        const picture = obraz.closest("picture");
        picture?.querySelectorAll("source").forEach((source) => {
          source.removeAttribute("srcset");
        });
        obraz.removeAttribute("srcset");
        obraz.removeAttribute("sizes");
        obraz.src = url.toString();
      };
      podglad.src = url.toString();
    }

    setFullImage(active) {
      this.isFullImage = active;
      if (active) this.zaladujPelnaRozdzielczosc();
      if (!active) this.resetZoom();
      // Zalegla flaga przeciagania zjadlaby pierwszy klik po wyjsciu z trybu.
      this.przeciagnieto = false;
      this.classList.toggle("product__modal-gallery--full-image", active);
      if (this.swiper) {
        this.swiper.allowTouchMove = !active;
        this.swiper.allowSlideNext = !active;
        this.swiper.allowSlidePrev = !active;
      }
    }

    onFullImageClick(event) {
      const trigger = event.target.closest("[data-modal-gallery-full-image]");
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      // Mobile ma tylko dwa stany: zwykla galeria oraz przyblizony, przesuwalny
      // obraz. Tap na przyblizonym wraca bezposrednio do zwyklej galerii.
      // Przeciagniecie nie trafi tutaj - odfiltrowuje je capture listener ponizej.
      if (this.isFullImage && this.ostatniTypWskaznika === "touch") {
        this.setFullImage(false);
        return;
      }

      // Dla myszy zachowujemy osobny zoom sterowany kolem/przeciagnieciem.
      if (this.isFullImage && this.zoom.skala > 1) {
        this.resetZoom();
        return;
      }

      const wchodzimy = !this.isFullImage;
      this.setFullImage(wchodzimy);

      // Na telefonie zdjecie poziome w trybie pelnym zajmowalo ok. 30% wysokosci ekranu.
      // Tapniecie od razu powieksza je do pelnej wysokosci, wysrodkowane w miejscu tapniecia.
      if (wchodzimy && this.ostatniTypWskaznika === "touch") {
        const punktX = event.clientX;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => this.wypelnijWysokosc(punktX)),
        );
      }
    }

    /* Rzeczywisty obszar zdjecia w elemencie <img> z object-fit: contain.
       Sam getBoundingClientRect() zwraca cale pudelko elementu, razem z pustymi
       pasami - granice przesuwania liczone z niego wypuszczaly kadr w tlo. */
    pudloZdjecia(obraz) {
      const r = obraz.getBoundingClientRect();
      const proporcja = obraz.naturalWidth && obraz.naturalHeight
        ? obraz.naturalWidth / obraz.naturalHeight
        : r.width / r.height;
      const szer = Math.min(r.width, r.height * proporcja);
      const wys = Math.min(r.height, r.width / proporcja);
      const left = r.left + (r.width - szer) / 2;
      const top = r.top + (r.height - wys) / 2;
      return { left, top, right: left + szer, bottom: top + wys, width: szer, height: wys };
    }

    wypelnijWysokosc(punktX) {
      const obraz = this.warstwaZoomu()?.querySelector("img");
      if (!obraz) return;
      const pudlo = this.pudloZdjecia(obraz);
      if (!pudlo.height) return;
      const skala = window.innerHeight / pudlo.height;
      if (skala < 1.1) return;
      this.zmienSkale(Math.min(this.zoomMax, skala), punktX, pudlo.top + pudlo.height / 2);
    }

    /* Na komputerze w trybie pelnym zdjecie powieksza sie pod kursorem i jedzie za nim.
       Skala zoomu zostaje 1, wiec klik dalej zamyka tryb pelny. */
    hoverZoom(event) {
      const warstwa = this.warstwaZoomu();
      if (!warstwa) return;
      // Pozycje liczymy z nieprzeskalowanego obszaru (przycisk trybu pelnego) -
      // pudelko samej warstwy jest juz powiekszone i przesuwaloby punkt zoomu.
      const obszar = warstwa.closest("[data-modal-gallery-full-image]") || warstwa;
      const pudlo = obszar.getBoundingClientRect();
      const px = Math.max(0, Math.min(100, ((event.clientX - pudlo.left) / pudlo.width) * 100));
      const py = Math.max(0, Math.min(100, ((event.clientY - pudlo.top) / pudlo.height) * 100));
      warstwa.style.transformOrigin = px + "% " + py + "%";
      warstwa.style.transform = "scale(" + this.hoverSkala + ")";
      this.classList.add("product__modal-gallery--hover-zoom");
    }

    koniecHoverZoomu() {
      const warstwa = this.warstwaZoomu();
      if (warstwa && this.zoom.skala === 1) {
        warstwa.style.transform = "";
        warstwa.style.transformOrigin = "";
      }
      this.classList.remove("product__modal-gallery--hover-zoom");
    }

    /* ---- powiekszanie w trybie pelnoekranowym ---- */

    warstwaZoomu() {
      const aktywny =
        this.swiper?.slides?.[this.swiper.activeIndex] ||
        this.querySelector(".swiper-slide-active");
      return aktywny?.querySelector("[data-modal-gallery-full-image] .media") || null;
    }

    resetZoom() {
      this.zoom = { skala: 1, x: 0, y: 0 };
      this.classList.remove("product__modal-gallery--zoomed");
      const warstwa = this.warstwaZoomu();
      if (warstwa) {
        warstwa.style.transform = "";
        warstwa.style.transformOrigin = "";
      }
      this.classList.remove("product__modal-gallery--hover-zoom");
    }

    nalozTransform(warstwa) {
      warstwa.style.transform =
        "scale(" + this.zoom.skala + ") translate(" + this.zoom.x + "px," + this.zoom.y + "px)";
    }

    /* Ile trzeba dolozyc w jednej osi, zeby zdjecie nie odslonilo tla.
       Liczymy z faktycznego polozenia zdjecia, bo obraz bywa wpisany w kontener
       niesymetrycznie i wyliczenie zapasu ze srodka chybialoby o te roznice.
       Gdy zdjecie jest mniejsze od okna, nie ma czego przesuwac - wracamy
       na pozycje naturalna. */
    korekta(poczatek, koniec, rozmiarOkna) {
      const rozmiar = koniec - poczatek;
      if (rozmiar <= rozmiarOkna) return null;
      if (poczatek > 0) return -poczatek;
      if (koniec < rozmiarOkna) return rozmiarOkna - koniec;
      return 0;
    }

    zastosujZoom() {
      const warstwa = this.warstwaZoomu();
      if (!warstwa) return;
      this.nalozTransform(warstwa);

      const obraz = warstwa.querySelector("img");
      if (obraz) {
        warstwa.style.transformOrigin = "";
        const pudlo = this.pudloZdjecia(obraz);
        const dx = this.korekta(pudlo.left, pudlo.right, window.innerWidth);
        const dy = this.korekta(pudlo.top, pudlo.bottom, window.innerHeight);
        this.zoom.x = dx === null ? 0 : this.zoom.x + dx / this.zoom.skala;
        this.zoom.y = dy === null ? 0 : this.zoom.y + dy / this.zoom.skala;
        if (dx || dy || dx === null || dy === null) this.nalozTransform(warstwa);
      }

      this.classList.toggle("product__modal-gallery--zoomed", this.zoom.skala > 1);
    }

    /* Powiekszamy wokol punktu pod kursorem, a nie wokol srodka - inaczej
       przy dojezdzaniu do rogu zdjecia kadr ucieka uzytkownikowi. */
    zmienSkale(nowaSkala, punktX, punktY) {
      const warstwa = this.warstwaZoomu();
      if (!warstwa) return;
      const docelowa = Math.max(1, Math.min(this.zoomMax, nowaSkala));
      if (docelowa === this.zoom.skala) return;

      const pudlo = warstwa.getBoundingClientRect();
      const srodekX = pudlo.left + pudlo.width / 2;
      const srodekY = pudlo.top + pudlo.height / 2;
      const odchyleniaX = (punktX - srodekX) / this.zoom.skala;
      const odchyleniaY = (punktY - srodekY) / this.zoom.skala;

      this.zoom.x -= odchyleniaX * (docelowa - this.zoom.skala) / docelowa;
      this.zoom.y -= odchyleniaY * (docelowa - this.zoom.skala) / docelowa;
      this.zoom.skala = docelowa;

      if (docelowa === 1) {
        this.zoom.x = 0;
        this.zoom.y = 0;
      }
      this.zastosujZoom();
    }

    onThumbnailClick(event) {
      const button = event.target.closest("[data-modal-gallery-thumbnail]");
      if (!button || !this.contains(button)) return;

      event.preventDefault();

      const targetIndex = this.galleryCore.getSlideIndexForMedia(
        this.swiper,
        this.modalSlides,
        button.dataset.modalGalleryThumbnail,
      );
      if (targetIndex < 0) return;
      this.swiper.slideTo(targetIndex);
    }

    syncActiveState() {
      this.galleryCore.syncActiveThumbnail({
        swiper: this.swiper,
        thumbnailSwiper: this.thumbnailSwiper,
        thumbnailSlides: this.thumbnailSlides,
        buttonSelector: "[data-modal-gallery-thumbnail]",
      });
    }

    listen() {
      this.querySelector(".product__modal-gallery__close")?.addEventListener("click", () => {
        this.close();
      });

      this._fullImageClickHandler = (event) => this.onFullImageClick(event);
      this._thumbnailClickHandler = (event) => this.onThumbnailClick(event);
      this.addEventListener("click", this._fullImageClickHandler);
      this.addEventListener("click", this._thumbnailClickHandler);

      this.sluchajZoomu();

      this._docClickHandler = (event) => {
        if (event.target.closest("model-viewer")) return;

        const clickedSlide = event.target.closest(".product__gallery-main .swiper-slide");
        if (!clickedSlide || clickedSlide.hidden) return;

        const gallery = event.target.closest("product-gallery");
        const activeSlide = gallery?.swiper?.slides?.[gallery.swiper.activeIndex];
        const slide = activeSlide && !activeSlide.hidden ? activeSlide : clickedSlide;

        this.open(slide.dataset.mediaIndex || slide.dataset.index);
      };
      document.addEventListener("click", this._docClickHandler);
    }

    /* Wskazniki trzymamy w mapie, zeby jednym kodem obsluzyc mysz, rysik
       i dwa palce - przy dwoch aktywnych wskaznikach robi sie szczypanie. */
    sluchajZoomu() {
      this.wskazniki = new Map();
      this.dystansStartowy = 0;
      this.skalaStartowa = 1;
      this.przeciagnieto = false;

      this.addEventListener(
        "wheel",
        (event) => {
          if (!this.isFullImage) return;
          if (!event.target.closest("[data-modal-gallery-full-image]")) return;
          event.preventDefault();
          const krok = event.deltaY < 0 ? 1.18 : 1 / 1.18;
          if (this.zoom.skala === 1) {
            this.hoverSkala = Math.max(1.5, Math.min(this.zoomMax, this.hoverSkala * krok));
            this.hoverZoom(event);
            return;
          }
          this.zmienSkale(this.zoom.skala * krok, event.clientX, event.clientY);
        },
        { passive: false },
      );

      this.addEventListener("pointerdown", (event) => {
        this.ostatniTypWskaznika = event.pointerType;
        if (!event.target.closest("[data-modal-gallery-full-image]")) return;

        // Poza trybem pelnego zdjecia sledzimy tylko dotyk: na telefonie klient od razu
        // szczypie zdjecie w galerii, nie wiedzac, ze najpierw trzeba je tapnac.
        // Drugi palec na zdjeciu przelacza wiec w tryb pelny i od razu powieksza.
        if (!this.isFullImage && event.pointerType !== "touch") return;
        this.wskazniki.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.przeciagnieto = false;

        if (!this.isFullImage) {
          if (this.wskazniki.size < 2) return;
          this.setFullImage(true);
          this.przeciagnieto = true;
        }

        if (this.wskazniki.size === 2) {
          this.dystansStartowy = this.dystansWskaznikow();
          this.skalaStartowa = this.zoom.skala;
        }
      });

      this.addEventListener("pointermove", (event) => {
        if (
          event.pointerType === "mouse" &&
          this.isFullImage &&
          this.zoom.skala === 1 &&
          event.buttons === 0
        ) {
          if (event.target.closest("[data-modal-gallery-full-image]")) this.hoverZoom(event);
          else this.koniecHoverZoomu();
          return;
        }
        const poprzedni = this.wskazniki.get(event.pointerId);
        if (!poprzedni || !this.isFullImage) return;
        const przesunieteX = event.clientX - poprzedni.x;
        const przesunieteY = event.clientY - poprzedni.y;
        this.wskazniki.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.wskazniki.size >= 2) {
          const teraz = this.dystansWskaznikow();
          if (this.dystansStartowy > 0) {
            const srodek = this.srodekWskaznikow();
            this.zmienSkale(
              (this.skalaStartowa * teraz) / this.dystansStartowy,
              srodek.x,
              srodek.y,
            );
          }
          this.przeciagnieto = true;
          return;
        }

        // Jednym wskaznikiem przesuwamy kadr, ale tylko gdy jest co przesuwac.
        if (this.zoom.skala <= 1) return;
        if (Math.abs(przesunieteX) > 2 || Math.abs(przesunieteY) > 2) this.przeciagnieto = true;
        this.zoom.x += przesunieteX / this.zoom.skala;
        this.zoom.y += przesunieteY / this.zoom.skala;
        this.zastosujZoom();
      });

      const koniec = (event) => {
        this.wskazniki.delete(event.pointerId);
        if (this.wskazniki.size < 2) this.dystansStartowy = 0;
      };
      this.addEventListener("pointerup", koniec);
      this.addEventListener("pointercancel", koniec);
      this.addEventListener("pointerleave", (event) => {
        koniec(event);
        if (event.pointerType === "mouse") this.koniecHoverZoomu();
      });

      // Klik konczacy przeciaganie nie moze byc wziety za klik zamykajacy.
      this.addEventListener(
        "click",
        (event) => {
          if (!this.przeciagnieto) return;
          this.przeciagnieto = false;
          event.stopPropagation();
        },
        true,
      );

      this._zoomKeyHandler = (event) => {
        if (this.style.display !== "block") return;
        if (event.key === "Escape") {
          if (this.zoom.skala > 1) this.resetZoom();
          else if (this.isFullImage) this.setFullImage(false);
          else this.close();
        }
      };
      document.addEventListener("keydown", this._zoomKeyHandler);
    }

    dystansWskaznikow() {
      const [a, b] = [...this.wskazniki.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    srodekWskaznikow() {
      const [a, b] = [...this.wskazniki.values()];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }

    open(mediaIndex = 0) {
      this.style.display = "block";
      document.body.style.overflow = "hidden";
      this.setFullImage(false);
      this.loadContent().then(() => {
        if (this.swiper) {
          this.filterMedia();
          this.swiper.update();

          const slides = Array.from(this.swiper.slides);
          const target = slides.find((slide) => !slide.hidden && slide.dataset.mediaIndex === String(mediaIndex));
          const targetIndex = target ? slides.indexOf(target) : 0;
          this.swiper.slideTo(targetIndex, 0);
          this.syncActiveState();
        }
        this.playActiveVideo();
      });
    }

    close() {
      this.pauseVideos();
      this.setFullImage(false);
      this.style.display = "none";
      document.body.style.overflow = "";
    }

    disconnectedCallback() {
      if (this._configurationHandler) {
        window.removeEventListener("pdpConfigurationChange", this._configurationHandler);
      }
      if (this._docClickHandler) {
        document.removeEventListener("click", this._docClickHandler);
      }
      if (this._fullImageClickHandler) {
        this.removeEventListener("click", this._fullImageClickHandler);
      }
      if (this._thumbnailClickHandler) {
        this.removeEventListener("click", this._thumbnailClickHandler);
      }
    }
  }

  customElements.define("product-modal-gallery", ProductModalGallery);
}
